import dayjs from 'dayjs';

import type { LinkFormValue } from './links';
import { pageTargetOf } from './links';
import type { ReportFilter } from './report/report';
import type {
  GiftProduct,
  InstitutionEvent,
  PartnersLinkList,
  PartnersLinkPage,
  PartnersMember,
  PartnersReport,
  Region,
} from './types';

const DEMO_CLICKS = [1_800, 2_400, 3_100, 2_700, 3_400, 2_600];
const DEMO_DONATIONS = [250, 290, 330, 300, 360, 320];

export const GUEST_MEMBER: PartnersMember = {
  suspendYN: false,
  hasLinkYN: false,
  partnerGrade: 'STARTER',
};

export const DEMO_REPORT: PartnersReport = {
  periodStart: '2026-10-01',
  totalRewardAmount: 3_700_000,
  scheduledRewardAmount: 3_700_000,
  paidRewardAmount: 0,
  clickCount: 16_000,
  donationCount: 1_850,
  conversionRate: 11.56,
  series: DEMO_CLICKS.map((clickCount, index) => ({
    bucketStartDate: dayjs('2026-10-01').add(index, 'day').format('YYYY-MM-DD'),
    clickCount,
    donationCount: DEMO_DONATIONS[index],
  })),
};

export const DEMO_FILTER: ReportFilter = {
  channel: '',
  start: '2026-10-01',
  end: '2026-10-06',
  isAll: false,
};

export const DEMO_LINK_LIST: PartnersLinkList = {
  donorCount: 1_850,
  rewardAmount: 3_700_000,
  contents: [
    {
      partnersLinkId: '0199e3a1-7c2b-7d3e-8f4a-1b2c3d4e5f61',
      code: 'aB3dE9',
      contentName: '10월 연말정산 준비 글',
      channel: 'NAVER_BLOG',
      targetType: 'PAGE',
      targetName: '고향사랑기부 안내페이지',
      createAt: '2026-10-02T09:10:00',
      clickCount: 5_200,
      donationCount: 600,
      donorCount: 600,
      rewardAmount: 1_200_000,
    },
    {
      partnersLinkId: '0199e3a1-7c2b-7d3e-8f4a-1b2c3d4e5f62',
      code: 'kP7xQ2',
      contentName: '제주 기부 추천 글',
      channel: 'NAVER_BLOG',
      targetType: 'REGION',
      targetName: '제주특별자치도',
      createAt: '2026-09-28T20:40:00',
      clickCount: 6_500,
      donationCount: 750,
      donorCount: 750,
      rewardAmount: 1_500_000,
    },
    {
      partnersLinkId: '0199e3a1-7c2b-7d3e-8f4a-1b2c3d4e5f63',
      code: 'mZ4vN8',
      contentName: '임실 치즈 후기',
      channel: 'INSTAGRAM',
      targetType: 'GIFT_PRODUCT',
      targetName: '임실 숙성 치즈 선물세트',
      createAt: '2026-09-18T11:00:00',
      clickCount: 4_300,
      donationCount: 500,
      donorCount: 500,
      rewardAmount: 1_000_000,
    },
  ],
};

export const DEMO_LINK_PAGES: PartnersLinkPage[] = [
  {
    partnersLinkPageId: 'page-home',
    pageName: '고향사랑기부 홈',
    pageDescription: '답례품·지정기부사업·이벤트를 한눈에',
    pagePath: '/donation',
  },
  {
    partnersLinkPageId: 'page-guide',
    pageName: '고향사랑기부 안내',
    pageDescription: '제도 설명과 세액공제, 기부 방법',
    pagePath: '/donation/guide',
  },
  {
    partnersLinkPageId: 'page-gifts',
    pageName: '답례품 전체 보기',
    pageDescription: '지역별·카테고리별 답례품 목록',
    pagePath: '/donation/gifts',
  },
  {
    partnersLinkPageId: 'page-business',
    pageName: '지정기부사업 목록',
    pageDescription: '기부금이 쓰이는 사업을 골라 기부',
    pagePath: '/donation/hometown-business',
  },
];

const [DEMO_PAGE] = DEMO_LINK_PAGES;

export const DEMO_FORM_VALUE: LinkFormValue = {
  choice: DEMO_PAGE.partnersLinkPageId,
  query: '',
  target: pageTargetOf(DEMO_PAGE),
  channel: 'NAVER_BLOG',
  name: '치즈 선물세트 소개',
};

export const DEMO_GIFT_PRODUCTS: GiftProduct[] = [
  {
    productNumber: 'IM-1204',
    productName: '임실 숙성 치즈 선물세트',
    institutionShortName: '전북 임실군',
    requiredDonationAmount: 30_000,
  },
  {
    productNumber: 'JJ-0310',
    productName: '제주 감귤 3kg 박스',
    institutionShortName: '제주 서귀포시',
    requiredDonationAmount: 30_000,
  },
  {
    productNumber: 'IM-1210',
    productName: '임실 치즈 체험 세트',
    institutionShortName: '전북 임실군',
    requiredDonationAmount: 45_000,
  },
  {
    productNumber: 'YG-0771',
    productName: '영광 모싯잎 송편 2kg',
    institutionShortName: '전남 영광군',
    requiredDonationAmount: 28_000,
  },
  {
    productNumber: 'HS-2201',
    productName: '횡성 한우 불고기 600g',
    institutionShortName: '강원 횡성군',
    requiredDonationAmount: 60_000,
  },
];

export const DEMO_REGIONS: Region[] = [
  {
    homeLoveGovCodeId: 50,
    fullName: '제주특별자치도',
    value: '제주특별자치도',
    institutionSlugId: '50',
  },
  {
    homeLoveGovCodeId: 52710,
    fullName: '전북 임실군',
    value: '임실군',
    institutionSlugId: '52710',
  },
  {
    homeLoveGovCodeId: 51730,
    fullName: '강원 횡성군',
    value: '횡성군',
    institutionSlugId: '51730',
  },
  {
    homeLoveGovCodeId: 46870,
    fullName: '전남 영광군',
    value: '영광군',
    institutionSlugId: '46870',
  },
];

const DEMO_EVENT_DEFAULTS = {
  backgroundColor: '',
  bannerImageUrl: '',
  endedYN: false,
  institutionId: 0,
  institutionName: '',
  summary: '',
  thumbnailUrl: '',
};

export const DEMO_EVENTS: InstitutionEvent[] = [
  {
    eventId: 2609,
    title: '제주 감귤 수확철 기획전',
    endDate: '2026-11-30',
  },
  {
    eventId: 2611,
    title: '연말정산 전액 세액공제 안내 이벤트',
    endDate: '2026-12-31',
  },
  {
    eventId: 2610,
    title: '첫 기부 답례품 추가 증정',
    endDate: '2026-10-31',
  },
].map((event) => ({ ...DEMO_EVENT_DEFAULTS, ...event }));
